/**
 * §8.9 API & Developer Resources — 8 items with Ready/Coming-soon
 * badges.  Explicit visual distinction for still-in-preview surfaces.
 */

import React from 'react'
import clsx from 'clsx'
import {
  KeyRound, Radio, Package, FileText, BookOpen, Send, Gauge, GitCommit, Sparkles, Terminal, Lock,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { API_ITEMS } from '../data'
import { TONE } from '../_shared'

const ICONS = { KeyRound, Radio, Package, FileText, BookOpen, Send, Gauge, GitCommit }

const STATUS_LABEL = {
  preview: { l: 'Preview',      c: 'text-amber-400',   b: 'border-amber-400/30',   bg: 'bg-amber-400/[0.08]' },
  soon:    { l: 'Coming soon',   c: 'text-zinc-400',    b: 'border-white/15',       bg: 'bg-white/[0.03]'    },
  ready:   { l: 'Ready',          c: 'text-emerald-400', b: 'border-emerald-400/25', bg: 'bg-emerald-400/[0.06]' },
}

export default function ApiDocs() {
  return (
    <section id="api" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>API &amp; Developer Resources</Eyebrow>
          <SectionHeading align="center"
            title={<>Build on top of{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Kalki Ai Cloud.</span></>}
            subtitle="Programmatic access to platform primitives — authentication, webhooks, SDKs and integration guides.  Some surfaces are in early preview — clearly flagged below."
          />
        </div>

        {/* preview status banner */}
        <div className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.05] px-5 md:px-6 py-4 backdrop-blur-xl flex items-start gap-3">
          <Terminal className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-100 leading-relaxed">
            <span className="font-semibold text-white">API access is currently in private preview.</span>{' '}
            Enterprise-plan customers can request keys — general availability is on the roadmap.
          </p>
        </div>

        {/* items grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {API_ITEMS.map((a) => {
            const tone = TONE[a.tone] || TONE.orange
            const Icon = ICONS[a.iconKey] || Sparkles
            const status = STATUS_LABEL[a.status] || STATUS_LABEL.soon
            const gated = a.status === 'soon'
            return (
              <div key={a.id} className={clsx(
                'relative rounded-2xl border p-5 backdrop-blur-xl overflow-hidden flex flex-col gap-3',
                'border-white/10 bg-white/[0.02] transition-all hover:border-white/20 hover:bg-white/[0.03]',
              )}>
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                  </span>
                  <span className={clsx('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-wider', status.b, status.bg, status.c)}>
                    {gated && <Lock className="h-3 w-3" />}
                    {status.l}
                  </span>
                </div>
                <div className="relative">
                  <div className="text-[14px] font-semibold text-white leading-tight">{a.label}</div>
                  <p className="text-[12px] text-zinc-400 leading-relaxed mt-1.5">{a.body}</p>
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
